/**
 * Code Block
 * Displays code snippets with syntax highlighting, line numbers, and copy button.
 */

import { registerBlockRenderer } from '../registry';
import { escapeHtml, createBlockWrapper } from '../utils';
import { iconCopy, iconCheck } from '../../icons';
import type { CodeData } from '../types';

// =============================================================================
// TYPE VALIDATION
// =============================================================================

function isValidCodeData(data: unknown): data is CodeData {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;

  // Must have code string
  if (typeof d.code !== 'string') return false;

  return true;
}

// =============================================================================
// RENDER FUNCTION
// =============================================================================

function renderCode(data: CodeData, container: HTMLElement): void {
  const wrapper = createBlockWrapper('code');

  const showLineNumbers = data.lineNumbers !== false; // Default true
  const showCopyButton = data.copyButton !== false; // Default true
  const language = data.language || 'text';

  let html = '';

  // Header with filename and copy button
  html += '<div class="pms-code-header">';
  if (data.filename) {
    html += `<span class="pms-code-filename">${escapeHtml(data.filename)}</span>`;
  }
  html += `<span class="pms-code-language">${escapeHtml(language)}</span>`;
  if (showCopyButton) {
    html += `<button class="pms-code-copy" aria-label="Copy code">
      <span class="pms-code-copy-icon">${iconCopy()}</span>
      <span class="pms-code-copy-text">Copy</span>
    </button>`;
  }
  html += '</div>';

  // Code container
  const containerStyle = data.maxHeight ? `max-height: ${escapeHtml(data.maxHeight)}; overflow-y: auto;` : '';
  html += `<div class="pms-code-container" style="${containerStyle}">`;

  // Process code lines
  const lines = (data.code || '').split('\n');
  const highlightLines = new Set(data.highlightLines || []);

  if (showLineNumbers) {
    html += '<pre class="pms-code-pre pms-code-with-lines"><code class="pms-code-content">';
    lines.forEach((line, index) => {
      const lineNum = index + 1;
      const isHighlighted = highlightLines.has(lineNum);
      html += `<span class="pms-code-line${isHighlighted ? ' pms-code-line-highlighted' : ''}"><span class="pms-code-line-number">${lineNum}</span><span class="pms-code-line-content">${escapeHtml(line) || ' '}</span></span>`;
    });
    html += '</code></pre>';
  } else {
    html += `<pre class="pms-code-pre"><code class="pms-code-content">${escapeHtml(data.code || '')}</code></pre>`;
  }

  html += '</div>';
  wrapper.innerHTML = html;

  // Copy button functionality
  if (showCopyButton) {
    const copyBtn = wrapper.querySelector('.pms-code-copy') as HTMLButtonElement;
    const copyText = wrapper.querySelector('.pms-code-copy-text') as HTMLSpanElement;
    const copyIcon = wrapper.querySelector('.pms-code-copy-icon') as HTMLSpanElement;
    if (copyBtn && copyText && copyIcon) {
      copyBtn.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(data.code || '');
          copyText.textContent = 'Copied!';
          copyIcon.innerHTML = iconCheck();
          copyBtn.classList.add('pms-code-copied');
          setTimeout(() => {
            copyText.textContent = 'Copy';
            copyIcon.innerHTML = iconCopy();
            copyBtn.classList.remove('pms-code-copied');
          }, 2000);
        } catch (err) {
          console.error('Failed to copy code:', err);
          copyText.textContent = 'Failed';
          setTimeout(() => {
            copyText.textContent = 'Copy';
          }, 2000);
        }
      });
    }
  }

  container.appendChild(wrapper);
}

// =============================================================================
// REGISTRATION
// =============================================================================

registerBlockRenderer('code', (data: unknown, container: HTMLElement) => {
  if (!isValidCodeData(data)) {
    console.warn('[code] Invalid data:', data);
    const wrapper = createBlockWrapper('code');
    wrapper.innerHTML = '<div class="pms-block-error">Invalid code data</div>';
    container.appendChild(wrapper);
    return;
  }

  renderCode(data, container);
});
